import React from 'react';
import type { RankingResultT } from '@mentimeter/visualizations';
import { Ranking } from '@mentimeter/visualizations';
import {
  SvgElement,
  VisualizationUpdater,
  useUniqueSelector,
} from '@mentimeter/question-modules-shared';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';

interface DataT extends RankingResultT {
  index: number;
}

interface Props {
  selector: string;
  data: DataT[];
  fillColors: string[];
  textColor: string;
  animationMultiplier: 0 | 1;
  translate: ReturnType<typeof useTranslations<'presenting.legacy'>>;
  isScoreHidden: boolean;
}

export const WIDTH = 1460;
export const HEIGHT = 540;

const C = React.memo<Props>(
  ({
    selector,
    data,
    fillColors,
    textColor,
    animationMultiplier,
    translate,
    isScoreHidden,
  }) => {
    const ranking = new Ranking({
      selector: `#${selector}`,
      data,
      fillColors,
      textColor,
      animationMultiplier,
      translate,
      width: WIDTH,
      height: HEIGHT,
      isScoreHidden,
    });

    return (
      <SvgElement aria-hidden viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
        <VisualizationUpdater updateFn={ranking.update} />
        <g id={selector} />
      </SvgElement>
    );
  },
);

interface RankingVisualizationProps {
  shouldAnimate: boolean;
  shouldHideResults: boolean;
  results: Array<{ label: string; id: number; score: number; index: number }>;
}

export const LegacyRankingVisualization = ({
  shouldAnimate,
  shouldHideResults,
  results,
}: RankingVisualizationProps) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();
  const translate = useTranslations('presenting.legacy');
  const selector = useUniqueSelector();

  return (
    <C
      selector={selector}
      data={results}
      fillColors={fillColors}
      textColor={textColor}
      animationMultiplier={shouldAnimate ? 1 : 0}
      translate={translate}
      isScoreHidden={shouldHideResults}
    />
  );
};
